// src/pages/Admin/OnboardingInsights.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '../../services/adminApi';
import styles from './OnboardingInsights.module.css';
import { DiagnosticSummaryCards } from './OnboardingDiagnosticsPanel';

const RANGES = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: 'all', label: 'All time' },
];

export default function OnboardingInsights() {
    const [range, setRange] = useState('30d');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [diag, setDiag] = useState(null);

    // Small diagnostic summary (full detail lives on Admin → Activation).
    useEffect(() => {
        let cancelled = false;
        adminApi.getOnboardingDiagnostics({ range, options: 'false' })
            .then(data => { if (!cancelled) setDiag(data); })
            .catch(() => { if (!cancelled) setDiag(null); });
        return () => { cancelled = true; };
    }, [range]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        adminApi.getOnboardingStats({ range })
            .then(data => { if (!cancelled) setStats(data); })
            .catch(err => { if (!cancelled) setError(err.message || 'Failed to load onboarding stats'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [range]);

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div>
                    <h1 className={styles.pageTitle}>Onboarding Insights</h1>
                    <p className={styles.subtitle}>
                        What new users pick during intro signup.
                        {stats?.trackingSince && (
                            <> Tracking since {new Date(stats.trackingSince).toLocaleDateString()}.</>
                        )}
                    </p>
                </div>
                <div className={styles.rangeGroup} role="group" aria-label="Time range">
                    {RANGES.map(r => (
                        <button
                            key={r.value}
                            type="button"
                            className={r.value === range ? styles.rangeBtnActive : styles.rangeBtn}
                            aria-pressed={r.value === range}
                            onClick={() => setRange(r.value)}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {diag && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>Where people drop out</h2>
                    <p className={styles.sectionNote}>
                        Onboarding starters in this window, by diagnostic outcome.{' '}
                        {diag.trackedFrom
                            ? <>Step-level data since {new Date(diag.trackedFrom).toLocaleDateString()}.</>
                            : <>Step-level data starts with the first build that sends it.</>}{' '}
                        <Link to="/admin/activation">Step funnel, filters and devices →</Link>
                    </p>
                    <DiagnosticSummaryCards summary={diag.summary} trackedFrom={diag.trackedFrom} />
                </section>
            )}

            {error && <div className={styles.error}>{error}</div>}
            {loading && !stats && <div className={styles.loading}>Loading onboarding stats...</div>}

            {stats && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`}>
                    {stats.totals.completed === 0 ? (
                        <div className={styles.empty}>
                            No onboarding selections recorded in this range yet.
                        </div>
                    ) : (
                        <>
                            <section className={styles.section}>
                                <div className={styles.statsGrid}>
                                    <StatCard
                                        label="Completions"
                                        value={stats.totals.completed}
                                        hint={`of ${stats.totals.signups} signups`}
                                    />
                                    <StatCard label="Completion Rate" value={`${stats.totals.completionRate}%`} />
                                    <StatCard
                                        label="Skipped Genres"
                                        value={`${stats.totals.noGenresRate}%`}
                                        hint={`${stats.totals.noGenres} users picked none`}
                                    />
                                    <StatCard
                                        label="Skipped Titles"
                                        value={`${stats.totals.noTitlesRate ?? 0}%`}
                                        hint={`${stats.totals.noTitles ?? 0} users picked none (optional since onboarding v2)`}
                                    />
                                    <StatCard
                                        label="Avg Titles Picked"
                                        value={stats.totals.avgTitles}
                                        hint={stats.totals.noTitles ? `${stats.totals.avgTitlesWhenPicked} among users who picked any` : undefined}
                                    />
                                    <StatCard label="Avg Genres Picked" value={stats.totals.avgGenres} />
                                    <StatCard
                                        label="Movie Picks"
                                        value={`${Math.round(stats.kindSplit.moviePct)}%`}
                                        hint={`vs ${Math.round(stats.kindSplit.tvPct)}% TV (${stats.kindSplit.movie} / ${stats.kindSplit.tv})`}
                                    />
                                </div>
                            </section>

                            <div className={styles.columns}>
                                <section className={styles.section}>
                                    <h2 className={styles.sectionTitle}>Top Genres</h2>
                                    <p className={styles.sectionNote}>% of users who completed onboarding and picked each genre</p>
                                    {stats.topGenres.length > 0 ? (
                                        <RankedBars
                                            rows={stats.topGenres.map(g => ({
                                                key: g.id,
                                                label: g.name,
                                                count: g.count,
                                                pct: g.pct,
                                            }))}
                                        />
                                    ) : (
                                        <div className={styles.empty}>No genres picked in this range.</div>
                                    )}
                                </section>

                                <section className={styles.section}>
                                    <h2 className={styles.sectionTitle}>Top Titles</h2>
                                    <p className={styles.sectionNote}>
                                        % of users who picked each title · {stats.totals.uniqueTitles} unique titles picked
                                    </p>
                                    <RankedBars
                                        rows={stats.topTitles.map(t => ({
                                            key: `${t.kind}:${t.tmdbId}`,
                                            label: `${t.title}${t.year ? ` (${t.year})` : ''}`,
                                            posterUrl: t.posterUrl,
                                            tag: t.kind === 'tv' ? 'TV' : 'Movie',
                                            count: t.count,
                                            pct: t.pct,
                                        }))}
                                        showPosters
                                    />
                                </section>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, hint }) {
    return (
        <div className={styles.statCard}>
            <div className={styles.statLabel}>{label}</div>
            <div className={styles.statValue}>{value}</div>
            {hint && <div className={styles.statHint}>{hint}</div>}
        </div>
    );
}

// Horizontal bars scaled to the top row so small differences stay readable;
// the exact count and % are printed on every row, so the list doubles as a table.
function RankedBars({ rows, showPosters = false }) {
    const max = Math.max(...rows.map(r => r.count), 1);
    return (
        <ol className={styles.rankList}>
            {rows.map((r, i) => (
                <li
                    key={r.key}
                    className={styles.rankRow}
                    title={`${r.label}: ${r.count} user${r.count === 1 ? '' : 's'} (${r.pct}%)`}
                >
                    <span className={styles.rank}>{i + 1}</span>
                    {showPosters && (
                        r.posterUrl
                            ? <img src={r.posterUrl} alt="" className={styles.poster} />
                            : <span className={styles.poster} />
                    )}
                    <div className={styles.rankBody}>
                        <div className={styles.rankLabelRow}>
                            <span className={styles.rankLabel}>{r.label}</span>
                            {r.tag && <span className={styles.tag}>{r.tag}</span>}
                            <span className={styles.rankValue}>
                                {r.count} <span className={styles.rankPct}>· {r.pct}%</span>
                            </span>
                        </div>
                        <div className={styles.barTrack}>
                            <div className={styles.bar} style={{ width: `${(r.count / max) * 100}%` }} />
                        </div>
                    </div>
                </li>
            ))}
        </ol>
    );
}
